import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MonoLabel } from "../components/kit";
import { EditorialCalendar } from "../components/EditorialCalendar";
import api, { formatApiErrorDetail } from "../lib/api";

export default function CalendarPage() {
  const navigate = useNavigate();
  const [brain, setBrain] = useState(null);
  const [calendar, setCalendar] = useState({ cadence: 3, slots: [] });
  const [captionBusyId, setCaptionBusyId] = useState(null);
  const [moments, setMoments] = useState([]);
  const [momentBusyKey, setMomentBusyKey] = useState(null);

  const loadCalendar = () => api.get("/calendar").then(({ data }) => setCalendar(data)).catch(() => {});
  useEffect(() => {
    api.get("/brand-brain").then(({ data }) => setBrain(data)).catch(() => {});
    loadCalendar();
    api.get("/calendar/marketing-moments").then(({ data }) => setMoments(data.items || [])).catch(() => {});
  }, []);

  const acceptMoment = async (moment) => {
    setMomentBusyKey(moment.key);
    try {
      const { data: slot } = await api.post("/calendar/marketing-moments/accept", { key: moment.key });
      setMoments((m) => m.filter((x) => x.key !== moment.key));
      api.post(`/calendar/slots/${slot.id}/caption`).then(loadCalendar).catch(() => {});
      await loadCalendar();
      return slot;
    } catch (err) {
      if (err.response?.status === 409) {
        setMoments((m) => m.filter((x) => x.key !== moment.key));
        await loadCalendar();
        return null;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add the moment — try again.");
      return null;
    } finally {
      setMomentBusyKey(null);
    }
  };

  const onMomentAccept = async (moment) => {
    const slot = await acceptMoment(moment);
    if (slot) toast(`"${moment.name}" is on your calendar.`);
  };

  const onMomentDismiss = async (key) => {
    const prev = moments;
    setMoments((m) => m.filter((x) => x.key !== key));
    try {
      await api.post("/calendar/marketing-moments/dismiss", { key });
    } catch {
      setMoments(prev);
      toast.error("Couldn't dismiss — try again.");
    }
  };

  const onMomentScript = async (moment) => {
    const slot = await acceptMoment(moment);
    if (slot) navigate("/app/organic/scripts", { state: { scriptPrefill: { idea: { titolo: slot.title, tipo: slot.tipo, punti: slot.punti, cta: slot.cta }, slotId: slot.id } } });
  };

  const onMomentCarousel = async (moment) => {
    const slot = await acceptMoment(moment);
    if (slot) navigate("/app/organic/carousel", { state: { carouselPrefill: { topic: `${slot.title} — ${slot.punti}`.slice(0, 160), pillar: null, slotId: slot.id } } });
  };

  const onCaption = async (slot) => {
    setCaptionBusyId(slot.id);
    try {
      await api.post(`/calendar/slots/${slot.id}/caption`);
      await loadCalendar();
      toast("Caption ready.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't write the caption — try again.");
    } finally {
      setCaptionBusyId(null);
    }
  };

  const onCaptionSave = async (id, caption) => {
    try {
      await api.patch(`/calendar/slots/${id}`, { caption });
      setCalendar((c) => ({ ...c, slots: c.slots.map((s) => (s.id === id ? { ...s, caption } : s)) }));
    } catch {
      toast.error("Couldn't save the caption.");
    }
  };

  if (brain === null) return null;

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="calendar-page">
      <MonoLabel>Organic Studio · Calendar</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Only what you chose to publish
      </h1>
      <p className="text-[13px] text-[#54505B] mt-4 max-w-xl">
        Nothing lands here automatically — add scripts and ideas from their modules, each with its own date and a ready-to-publish caption.
      </p>
      <EditorialCalendar
        calendar={calendar}
        pillars={brain?.content_pillars || []}
        moments={moments}
        momentBusyKey={momentBusyKey}
        onMomentAccept={onMomentAccept}
        onMomentDismiss={onMomentDismiss}
        onMomentScript={onMomentScript}
        onMomentCarousel={onMomentCarousel}
        busy={false}
        onCadence={async (c) => { const { data } = await api.put("/calendar/cadence", { cadence: c }); setCalendar(data); }}
        onMove={async (id, date) => { await api.patch(`/calendar/slots/${id}`, { date }); loadCalendar(); }}
        onStatus={async (id, status) => { await api.patch(`/calendar/slots/${id}`, { status }); loadCalendar(); }}
        onDelete={async (id) => { await api.delete(`/calendar/slots/${id}`); loadCalendar(); }}
        onAddSlot={async (title, date) => { await api.post("/calendar/slots", { title, date }); loadCalendar(); }}
        onScript={(slot) => navigate("/app/organic/scripts", { state: { scriptPrefill: { idea: { titolo: slot.title, tipo: slot.tipo, punti: slot.punti, cta: slot.cta }, slotId: slot.id } } })}
        onCarousel={(slot) => navigate("/app/organic/carousel", { state: { carouselPrefill: { topic: slot.title, pillar: slot.pillar || null, slotId: slot.id } } })}
        onCaption={onCaption}
        onCaptionSave={onCaptionSave}
        captionBusyId={captionBusyId}
      />
    </div>
  );
}
